import { useEffect, useState } from "react";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  SelectChangeEvent,
  SelectProps,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../reduxStore/Store";
import { SysTypeDocument } from "../../models/models";
import { setSelectedTypeDoc, fetchSysTypeDocuments } from "../../reduxStore/SysTypeDocumentSlice";
import { fetchAllowedSysTypeDocuments } from "../../reduxStore/ScopeSlice";

interface SelectSysTypeDocumentProps
  extends Omit<SelectProps<number | string>, "value" | "onChange"> {
  onSysTypeChange: (selected: SysTypeDocument | null) => void;
  selectedSysType?: SysTypeDocument | null;
  initialId?: number | null;
  showAll?: boolean;
}

const SelectSysTypeDocument: React.FC<SelectSysTypeDocumentProps> = ({
  onSysTypeChange,
  selectedSysType,
  initialId,
  showAll = false,
  ...selectProps
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const sysTypeData = useSelector((state: RootState) => state.ScopeSlice.allowedSysTypeDocuments);
  const fullSysTypeData = useSelector((state: RootState) => state.SysTypeDocumentSlice.sysTypeData);
  const [selectedId, setSelectedId] = useState<number | string>(initialId ?? "");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      dispatch(fetchAllowedSysTypeDocuments()).unwrap(),
      dispatch(fetchSysTypeDocuments()).unwrap(),
    ])
      .catch((err: any) => setError(typeof err === 'string' ? err : 'Erreur chargement types autorisés.'))
      .finally(() => setLoading(false));
  }, [dispatch]);

  // "Tous les documents" n'est proposé que s'il n'y a aucune restriction
  // (sinon il serait trompeur — la requête ne renverrait pas tout pour ce user)
  const hasRestrictions = fullSysTypeData.length > 0 && sysTypeData.length < fullSysTypeData.length;
  const canShowAll = showAll && !hasRestrictions;

  useEffect(() => {
    if (!selectedSysType && sysTypeData.length > 0 && selectedId === "") {
      // Auto-sélection du premier type si "Tous les documents" n'est pas proposé
      if (!canShowAll) {
        const firstItem = sysTypeData[0];
        setSelectedId(firstItem.id);
        onSysTypeChange(firstItem);
        dispatch(setSelectedTypeDoc(firstItem));
      }
    } else if (selectedId !== "" && sysTypeData.length > 0) {
      // Restaure la sélection après rechargement des données (ex: remontage du composant)
      const found = sysTypeData.find((item) => item.id === Number(selectedId));
      if (found) {
        onSysTypeChange(found);
        dispatch(setSelectedTypeDoc(found));
      }
    }
  }, [sysTypeData, canShowAll]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (event: SelectChangeEvent<string | number>) => {
    const id = event.target.value;
    const selected = sysTypeData.find((item) => item.id === Number(id)) || null;
    setSelectedId(id);
    onSysTypeChange(selected);
    dispatch(setSelectedTypeDoc(selected));
  };

  return (
    <FormControl fullWidth size="small" variant="outlined" error={!!error} required>
      <InputLabel id="select-sys-type-label">Type de document</InputLabel>
      <Select
        labelId="select-sys-type-label"
        value={selectedId}
        onChange={handleChange}
        label="Type de document"
        disabled={loading || sysTypeData.length === 0}
        size="small"
        {...selectProps}
      >
        {canShowAll && (
          <MenuItem value="">
            <em>Tous les documents</em>
          </MenuItem>
        )}
        {sysTypeData.map((type) => (
          <MenuItem key={type.id} value={type.id}>
            {type.libelle}
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText>{error}</FormHelperText>}
    </FormControl>
  );
};

export default SelectSysTypeDocument;
